import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';

import { NotificationsService } from 'app/layout/components/navbar/navbar-notification/notifications.service';
import { PushNotificationService } from 'app/services/push-notification.service';
import Swal from 'sweetalert2';

// Interface
interface notification {
  messages: any[];
  systemMessages: any[];
  system: Boolean;
}

interface NotificationMessage {
  id?: string;
  image: string;
  heading: string;
  text: string;
  read?: boolean;
  url?: string;
  is_read?: boolean;
  created_at?: string;
}

@Component({
  selector: 'app-navbar-notification',
  templateUrl: './navbar-notification.component.html',
  styles: [`
    .notification-read {
      background-color: #f8f9fa !important;
      cursor: pointer !important;
    }
    
    .notification-read:hover {
      background-color: #e9ecef !important;
    }
    
    .notification-unread {
      background-color: #ffffff;
      border-left: 3px solid #7367f0;
      cursor: pointer;
    }
    
    .notification-unread:hover {
      background-color: #f8f9fa;
    }
    
    .scrollable-container {
      max-height: 400px;
      overflow-y: auto;
    }
    
    .dropdown-menu-footer .btn {
      font-size: 0.85rem;
    }
    
    .pagination-info {
      font-size: 0.75rem;
    }
  `]
})
export class NavbarNotificationComponent implements OnInit {
  // ViewChild reference to dropdown
  @ViewChild('notificationDropdown', { static: false }) notificationDropdown: NgbDropdown;
  
  // Public
  public notifications: notification;
  private previousNotificationCount: number = 0;
  private isInitialLoad: boolean = true;
  
  // Paginação
  public currentPage: number = 1;
  public itemsPerPage: number = 10;
  public totalNotifications: number = 0;
  public paginatedMessages: NotificationMessage[] = [];
  public paginatedSystemMessages: NotificationMessage[] = [];

  /**
   *
   * @param {NotificationsService} _notificationsService
   * @param {PushNotificationService} _pushNotificationService
   * @param {Router} router
   */
  constructor(
    private _notificationsService: NotificationsService,
    private _pushNotificationService: PushNotificationService,
    private router: Router
  ) {}

  // Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this._notificationsService.onApiDataChange.subscribe(res => {
      const currentNotificationCount = this.calculateNotificationCount(res);
      
      // Check if there are new notifications (skip initial load)
      if (!this.isInitialLoad && currentNotificationCount > this.previousNotificationCount) {
        this.showNewNotificationAlert(res);
      }
      
      this.notifications = res;
      this.sortAndPaginateNotifications();
      this.previousNotificationCount = currentNotificationCount;
      this.isInitialLoad = false; // Mark that initial load is complete
    });
  }

  // Public Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Mark individual notification as read
   * @param message - The notification message to mark as read
   */
  markAsRead(message: NotificationMessage): void {
    if (message.id) {
      // Call service to mark as read on backend
      this._notificationsService.markNotificationAsRead(message.id).then(() => {
        // Remove from local array or mark as read
        this.removeNotificationFromArray(message);
      }).catch(error => {
        console.error('Erro ao marcar notificação como lida:', error);
      });
    } else {
      // If no ID, just remove from local array
      this.removeNotificationFromArray(message);
    }

    // If notification has URL, navigate to it using Angular Router
    if (message.url) {
      this.navigateToNotificationUrl(message.url);
      // Close dropdown after navigation
      this.closeDropdown();
    }
  }

  /**
   * Close the notification dropdown
   */
  private closeDropdown(): void {
    try {
      if (this.notificationDropdown) {
        this.notificationDropdown.close();
      }
    } catch (error) {
      console.error('Erro ao fechar dropdown:', error);
    }
  }

  /**
   * Navigate to notification URL using Angular Router
   * @param url - The URL to navigate to
   */
  private navigateToNotificationUrl(url: string): void {
    try {
      // Remove leading slash if present for Angular Router
      const routeUrl = url.startsWith('/') ? url.substring(1) : url;
      
      // Use Angular Router for smooth navigation
      this.router.navigateByUrl(routeUrl).then(success => {
        // Navigation successful
      }).catch(error => {
        console.error('Erro na navegação do Router:', error);
        // Fallback to window.location if Router fails
        window.location.href = url;
      });
    } catch (error) {
      console.error('Erro ao navegar para URL da notificação:', error);
      // Fallback to window.location if Router fails
      window.location.href = url;
    }
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): void {
    this._notificationsService.markAllNotificationsAsRead().then(() => {
      // Clear all notifications
      this.notifications.messages = [];
      this.notifications.systemMessages = [];
    }).catch(error => {
      console.error('Erro ao marcar todas as notificações como lidas:', error);
      // Even if backend call fails, clear locally for better UX
      this.notifications.messages = [];
      this.notifications.systemMessages = [];
    });
  }

  /**
   * Remove notification from local array
   * @param messageToRemove - The message to remove
   */
  private removeNotificationFromArray(messageToRemove: NotificationMessage): void {
    // Remove from messages array
    this.notifications.messages = this.notifications.messages.filter(
      (msg: NotificationMessage) => msg !== messageToRemove
    );
    
    // Remove from systemMessages array
    this.notifications.systemMessages = this.notifications.systemMessages.filter(
      (msg: NotificationMessage) => msg !== messageToRemove
    );
  }

  /**
   * Get total unread notification count
   */
  getTotalNotificationCount(): number {
    return this.calculateNotificationCount(this.notifications);
  }

  /**
   * Test method to force show notification alert (for debugging)
   */
  testNotificationAlert(): void {
    console.log('DEBUG - Testing notification alert manually');
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'info',
      title: 'Teste de Notificação',
      text: 'Este é um teste do sistema de alertas',
      showConfirmButton: false,
      timer: 4000,
      timerProgressBar: true,
      customClass: {
        popup: 'colored-toast'
      }
    }).then(() => {
      console.log('- Test alert completed');
    });
  }

  /**
   * Simulate receiving a new notification (for testing)
   */
  simulateNewNotification(): void {
    console.log('DEBUG - Simulating new notification arrival');
    
    // Create a fake new notification
    const fakeNotification = {
      id: 'test-' + Date.now(),
      image: 'assets/images/portrait/small/avatar-s-11.jpg',
      heading: 'Nova Notificação Simulada',
      text: 'Esta é uma simulação de nova notificação chegando',
      read: false
    };

    // Add to current notifications
    if (!this.notifications.messages) {
      this.notifications.messages = [];
    }
    this.notifications.messages.push(fakeNotification);

    // Trigger the alert system by manually calling the subscription logic
    const currentCount = this.calculateNotificationCount(this.notifications);
    console.log('- Simulated count:', currentCount);
    console.log('- Previous count was:', this.previousNotificationCount);
    
    if (!this.isInitialLoad && currentCount > this.previousNotificationCount) {
      console.log('- Triggering alert for simulated notification');
      this.showNewNotificationAlert(this.notifications);
    }
    
    this.previousNotificationCount = currentCount;
  }

  /**
   * Simulate receiving multiple notifications at once (for testing)
   */
  simulateMultipleNotifications(): void {
    console.log('DEBUG - Simulating multiple notifications arrival');
    
    // Create multiple fake notifications
    const fakeNotifications = [
      {
        id: 'test-1-' + Date.now(),
        image: 'assets/images/portrait/small/avatar-s-11.jpg',
        heading: 'Primeira Notificação',
        text: 'Esta é a primeira notificação do lote',
        read: false
      },
      {
        id: 'test-2-' + Date.now(),
        image: 'assets/images/portrait/small/avatar-s-12.jpg',
        heading: 'Segunda Notificação',
        text: 'Esta é a segunda notificação do lote',
        read: false
      },
      {
        id: 'test-3-' + Date.now(),
        image: 'assets/images/portrait/small/avatar-s-13.jpg',
        heading: 'Terceira Notificação',
        text: 'Esta é a terceira notificação do lote',
        read: false
      }
    ];

    // Add to current notifications
    if (!this.notifications.messages) {
      this.notifications.messages = [];
    }
    this.notifications.messages.push(...fakeNotifications);

    // Trigger the alert system
    const currentCount = this.calculateNotificationCount(this.notifications);
    console.log('- Simulated count:', currentCount);
    console.log('- Previous count was:', this.previousNotificationCount);
    
    if (!this.isInitialLoad && currentCount > this.previousNotificationCount) {
      console.log('- Triggering alerts for multiple notifications');
      this.showNewNotificationAlert(this.notifications);
    }
    
    this.previousNotificationCount = currentCount;
  }

  /**
   * Sort notifications by date and paginate them
   */
  private sortAndPaginateNotifications(): void {
    // Combine all notifications
    const allMessages = [...(this.notifications?.messages || []), ...(this.notifications?.systemMessages || [])];
    
    // Sort by created_at date (newest first)
    const sortedMessages = allMessages.sort((a, b) => {
      const dateA = new Date(a.created_at || '').getTime();
      const dateB = new Date(b.created_at || '').getTime();
      return dateB - dateA; // Newest first
    });
    
    this.totalNotifications = sortedMessages.length;
    
    // Calculate pagination
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    
    // Get paginated results
    const paginatedResults = sortedMessages.slice(startIndex, endIndex);
    
    // Separate back into messages and systemMessages for display
    this.paginatedMessages = paginatedResults.filter(msg => 
      (this.notifications?.messages || []).includes(msg)
    );
    this.paginatedSystemMessages = paginatedResults.filter(msg => 
      (this.notifications?.systemMessages || []).includes(msg)
    );
  }

  /**
   * Go to specific page
   */
  goToPage(page: number): void {
    if (page >= 1 && page <= this.getTotalPages()) {
      this.currentPage = page;
      this.sortAndPaginateNotifications();
    }
  }

  /**
   * Go to next page
   */
  nextPage(): void {
    if (this.currentPage < this.getTotalPages()) {
      this.currentPage++;
      this.sortAndPaginateNotifications();
    }
  }

  /**
   * Go to previous page
   */
  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.sortAndPaginateNotifications();
    }
  }

  /**
   * Get total number of pages
   */
  getTotalPages(): number {
    return Math.ceil(this.totalNotifications / this.itemsPerPage);
  }

  /**
   * Check if notification is read
   */
  isNotificationRead(message: NotificationMessage): boolean {
    return message.is_read === true;
  }

  /**
   * Format notification date for display
   */
  formatNotificationDate(dateString?: string): string {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
      
      if (diffInMinutes < 1) {
        return 'Agora';
      } else if (diffInMinutes < 60) {
        return `${diffInMinutes}m atrás`;
      } else if (diffInMinutes < 1440) { // 24 hours
        const hours = Math.floor(diffInMinutes / 60);
        return `${hours}h atrás`;
      } else {
        const days = Math.floor(diffInMinutes / 1440);
        if (days === 1) {
          return 'Ontem';
        } else if (days < 7) {
          return `${days} dias atrás`;
        } else {
          return date.toLocaleDateString('pt-BR', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric' 
          });
        }
      }
    } catch (error) {
      return '';
    }
  }

  /**
   * Calculate total unread notification count
   * @param notificationData - The notification data to count
   */
  private calculateNotificationCount(notificationData: notification): number {
    const unreadMessages = (notificationData?.messages || []).filter((msg: NotificationMessage) => !msg.is_read).length;
    const unreadSystemMessages = (notificationData?.systemMessages || []).filter((msg: NotificationMessage) => !msg.is_read).length;
    return unreadMessages + unreadSystemMessages;
  }

  /**
   * Show alert for new notifications
   * @param notificationData - The notification data containing new notifications
   */
  private showNewNotificationAlert(notificationData: notification): void {
    const currentNotificationCount = this.calculateNotificationCount(notificationData);
    const newNotificationsCount = currentNotificationCount - this.previousNotificationCount;
    
    if (newNotificationsCount > 0) {
      // Verifica se deve exibir o alerta (não exibe para notificações de limite de compras)
      if (this._pushNotificationService.shouldShowAlert()) {
        console.log('🔔 Exibindo alerta de novas notificações');
        // Show single toast with total count of pending notifications
        this.showSingleNotificationToast(currentNotificationCount);
      } else {
        console.log('🔕 Alerta suprimido - notificação de limite de compras');
      }
    }
  }

  /**
   * Show single toast notification with total count of pending notifications
   * @param totalCount - Total number of pending notifications
   */
  private showSingleNotificationToast(totalCount: number): void {
    const message = totalCount === 1 
      ? 'Você tem 1 nova notificação' 
      : `Você tem ${totalCount} novas notificações`;

    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'info',
      title: 'Novas Notificações',
      text: message,
      showConfirmButton: false,
      timer: 4000,
      timerProgressBar: true,
      customClass: {
        popup: 'colored-toast'
      }
    });
  }
}
